import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import TopBar from "@/components/TopBar";
import TopBarV2 from "@/components/TopBarV2";
import NavbarV2 from "@/components/NavbarV2";
import Navbar from "@/components/Navbar";
import AfterpayBanner, { FloatingAfterpayBadge, AfterpayInfoSection } from "@/components/AfterpayBanner";
import HeroSection from "@/components/HeroSection";
import AugustCrownPromoCard from "@/components/AugustCrownPromoCard";
import HeroV2Section from "@/components/HeroV2Section";
import ReturningClientHero from "@/components/ReturningClientHero";
import TrustBar from "@/components/TrustBar";
import OurPhilosophySection from "@/components/OurPhilosophySection";
import CredentialsBadges from "@/components/CredentialsBadges";
import BrandsBanner from "@/components/BrandsBanner";
import PromotionsSection from "@/components/PromotionsSection";
import ServicesSection from "@/components/ServicesSection";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import AboutSection from "@/components/AboutSection";
import LocationsSection from "@/components/LocationsSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import GoogleReviewsSection from "@/components/GoogleReviewsSection";
import NewClientSection from "@/components/NewClientSection";
import CompactCommunityBanner from "@/components/CompactCommunityBanner";
import FinalCTASection from "@/components/FinalCTASection";
import ClientJourney from "@/components/ClientJourney"; // eslint-disable-line no-unused-vars
import TreatmentCategoriesGrid from "@/components/TreatmentCategoriesGrid";
import StickyBookButton from "@/components/StickyBookButton";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import BookingCart from "@/components/BookingCart";
import BookingLocationsModal from "@/components/BookingLocationsModal";
import InlineBookingFlow from "@/components/InlineBookingFlow";
import ProviderChooserModal from "@/components/ProviderChooserModal";
import VirtualConsultationModal from "@/components/VirtualConsultationModal";
import LastMinuteClubModal from "@/components/LastMinuteClubModal";
import LastMinuteBookingFlow from "@/components/LastMinuteBookingFlow";
import LastMinuteAtAGlance from "@/components/LastMinuteAtAGlance";
import TreatmentQuiz from "@/components/TreatmentQuiz";
import SocialProofNotification from "@/components/SocialProofNotification";
// perf: 276 KB scanner split into its own chunk; loads right after the page, same behaviour.
const AISkinAnalysis = lazy(() => import("@/components/AISkinAnalysis"));
import AIFutureRotatingFace from "@/components/AIFutureRotatingFace";
import CurrentlyViewing from "@/components/CurrentlyViewing";
import BeautyHubHomeSection from "@/components/BeautyHubHomeSection";
import MostPopularSection from "@/components/MostPopularSection";
import NowOfferingRotation from "@/components/NowOfferingRotation";
import TreatmentBoxMenu from "@/components/TreatmentBoxMenu";
import TreatmentMarquee from "@/components/TreatmentMarquee";
import BundleBuilder from "@/components/BundleBuilder";
import SeasonalSuggestions from "@/components/SeasonalSuggestions";
import WelcomeRewardsPopup from "@/components/WelcomeRewardsPopup";
import FacebookFeedWidget from "@/components/FacebookFeedWidget";
import HomeCareersSection from "@/components/HomeCareersSection";
import HomeSkincareTipsSection from "@/components/HomeSkincareTipsSection";
import WaitlistModal from "@/components/WaitlistModal";
import MobileQuickActions from "@/components/MobileQuickActions";
import PricingPickerPill from "@/components/PricingPickerPill";
import FloatingSocialProof from "@/components/FloatingSocialProof";
import KarineDailyBlog from "@/components/KarineDailyBlog";
import KarineDailyTip from "@/components/KarineDailyTip";
import DailyTipsPromo from "@/components/DailyTipsPromo";
import { CartProvider } from "@/context/CartContext";
import { Sparkles, Camera, MapPin, Crown, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { BLACK_FRIDAY_TEASER_IMG, isBlackFridayTeaserActive } from "@/data/blackFridayTeaser";
import BlackFridayMobileSplash from "@/components/promos/BlackFridayMobileSplash";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// FacialVisionAI Prewarm — fires a lightweight `/api/health` ping so the
// backend pod is hot by the time the client opens the AI modal. Debounced
// via `__aiPrewarmed` on window so we don't hammer the endpoint on every
// hover across multiple pages / re-renders. Silent on failure.
const _prewarmFacialVisionAI = () => {
  if (typeof window === "undefined") return;
  if (window.__aiPrewarmed && Date.now() - window.__aiPrewarmed < 60_000) return;
  window.__aiPrewarmed = Date.now();
  fetch(`${API}/health`, { method: "GET", cache: "no-store" }).catch(() => {});
};

const HomePage = () => {
  const { language } = useLanguage();
  // Prewarm the FacialVisionAI backend as soon as the homepage loads so
  // the pod is hot BEFORE the client opens the AI modal. Cold-start on
  // production has been the #1 cause of "Connection error" timeouts.
  useEffect(() => {
    _prewarmFacialVisionAI();
  }, []);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  // ProviderChooserModal — intercepts BOOK NOW so the client first picks
  // Karine (all locations, full menu) or Tanya (Ottawa only, laser/RF/HF).
  const [showProviderChooser, setShowProviderChooser] = useState(false);
  const [showVirtualModal, setShowVirtualModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  // Iter 172 — carries treatment/location prefill from the "Notify me
  // when a slot opens" CTA on service pages into WaitlistModal.
  const [waitlistPrefill, setWaitlistPrefill] = useState({ treatment: null, location: null });
  const [showQuiz, setShowQuiz] = useState(false);
  const [showSkinAnalysis, setShowSkinAnalysis] = useState(false);

  // When the booking modal is opened via /book?provider=<slug>, this state
  // carries the provider slug through to InlineBookingFlow so the cross-
  // provider suggestion engine knows which calendar to check first.
  const [bookingProvider, setBookingProvider] = useState(null);
  // Booking type — "aesthetic" or "medical". Narrows the treatment list
  // when a teammate runs distinct practices (e.g. an NP doing both
  // aesthetics AND women's health medical).
  const [bookingType, setBookingType] = useState(null);
  const [bookingSearch, setBookingSearch] = useState(null);
  // Pre-selected location id when /book is entered with ?location=1|2|3
  // (e.g. from the Virtual Consultation modal handoff — Jul 9 2026 fix).
  const [bookingLocationId, setBookingLocationId] = useState(null);
  // Hard-locked location — used when the source page ONLY runs at Ottawa
  // (Éminence Facials & Body Rituals, Tanya's HydraFacial / RF / Laser
  // funnels, etc). When set, InlineBookingFlow suppresses the 3-city
  // picker entirely so the client can't switch to Kanata or Carleton
  // Place for a service that isn't offered there. Feb 28 2026 (Karine):
  // "Éminence is Ottawa only — don't offer the other clinics".
  const [bookingLockLocationId, setBookingLockLocationId] = useState(null);
  // AI Skin Analysis "Book This Look" handoff — carries the exact
  // treatments the client just previewed so the InlineBookingFlow can
  // auto-add all of them to the cart in one shot.
  const [bookingPackage, setBookingPackage] = useState(null);
  // Sept 4 2026 · Patricia Russell bug fix — the /spin-and-win landing
  // page hands off with `?prize_code=<CODE>&qualifying=<csv>` so the
  // booking flow can auto-attach the code and pre-filter the treatment
  // picker. Read from URL below in the /book effect.
  const [bookingPrizeCode, setBookingPrizeCode] = useState(null);
  const [bookingPrizeQualifying, setBookingPrizeQualifying] = useState(null);

  // Last Minute Club — modal state + live spot count for the hero badge.
  const [showLastMinuteClub, setShowLastMinuteClub] = useState(false);
  const [showLastMinuteBooking, setShowLastMinuteBooking] = useState(false);
  const [lastMinuteCount, setLastMinuteCount] = useState(0);
  // Slot payload (selected from LMC or returned as alternative) passed to LastMinuteBookingFlow.
  const [lastMinutePrefill, setLastMinutePrefill] = useState(null);

  // Floating tile rotation (Jun 22, 2026): the bottom-right tile flips
  // between "Karine's 1-Minute Skin Tip" and a "Today's Promotions"
  // teaser every ~9s. Each face routes to its own page on tap, so the
  // single 192-px slot doubles as a sales driver without crowding the
  // UI. Index 0 = Skin Tip, 1 = Promotions, 2 = Women's Health · GLP-1 · Menopause.
  const [seqPos, setSeqPos] = useState(0);
  // Nov 3 2026 · Black Friday teaser is the 10th face until Nov 28 2026, and
  // (Karine, Nov 3) it shows after EVERY other face: BF, 0, BF, 1, BF, 2 …
  const bfTeaser = isBlackFridayTeaserActive();
  const seqLen = bfTeaser ? 18 : 9;
  const tileIndex = bfTeaser ? (seqPos % 2 === 0 ? 9 : Math.floor(seqPos / 2)) : seqPos;
  const tileCount = bfTeaser ? 10 : 9;
  // Oct 8 2026 · Karine — the FacialVisionAI pill rotates with Lumi
  // (skincare specialist) every ~6s. Lumi face dispatches `open-lumi`.
  const [visionLumiFace, setVisionLumiFace] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setVisionLumiFace((f) => (f + 1) % 2), 6000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => setSeqPos((i) => (i + 1) % seqLen), bfTeaser ? 7000 : 9000);
    return () => clearInterval(id);
  }, [seqLen, bfTeaser]);

  // Auto-open the Last Minute Club modal when the user lands on `/last-minute`
  // (or any of its aliases) — gives Karine a shareable IG/SMS-friendly link.
  const _location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const path = (_location?.pathname || "").toLowerCase();
    const lmcPaths = ["/last-minute", "/lastminute"];
    if (lmcPaths.includes(path)) {
      setShowLastMinuteClub(true);
      // Scroll to top so the modal appears centered on the hero, not mid-page.
      try { window.scrollTo({ top: 0, behavior: "instant" }); } catch { /* noop */ }
    }
    // Viral share landing — `?ai=1` (from "Send AI to a Friend") auto-opens
    // the Skin Analysis modal so referred friends drop straight into the
    // tool instead of the homepage. UTMs stay in the URL for analytics.
    try {
      const p = new URLSearchParams(_location?.search || "");
      if (p.get("ai") === "1") {
        setShowSkinAnalysis(true);
        try { window.scrollTo({ top: 0, behavior: "instant" }); } catch { /* noop */ }
      }
    } catch { /* noop */ }
    // /book?provider=<slug>&type=aesthetic|medical|consultation&location=1|2|3
    // — auto-open the booking modal with those defaults. Added `location`
    // and `type=consultation` on Jul 9 2026 so the Virtual Consultation
    // modal (which used to send clients to a broken tinyurl → Square
    // sales page) can hand off straight into our own /book flow.
    if (path === "/book") {
      try {
        const params = new URLSearchParams(_location.search || "");
        let prov = (params.get("provider") || "").toLowerCase();
        // Backwards-compat: old bookmarks / email links use `?provider=anna`
        // (she left Feb 2026 · Tanya took over the aesthetician role).
        if (prov === "anna") {
          prov = "tanya";
        }
        // Éminence page & Lumi spa-ritual cards pass `provider=aesthetician`.
        // The InlineBookingFlow filter engine is keyed off the slug `"tanya"`
        // (Tanya is the current Ottawa aesthetician) — remap so those flows
        // hit Tanya's narrow facial/body/laser catalog instead of Karine's
        // full Botox/filler menu. If a new aesthetician is ever hired,
        // change this alias in one place. (Feb 28 2026 · Karine bug report.)
        if (prov === "aesthetician") {
          prov = "tanya";
        }
        const type = (params.get("type") || "").toLowerCase();
        const loc = (params.get("location") || "").trim();
        if (prov) {
          setBookingProvider(prov);
        }
        if (type === "aesthetic" || type === "medical" || type === "consultation") {
          setBookingType(type);
        }
        if (loc && ["1", "2", "3"].includes(loc)) {
          setBookingLocationId(loc);
        }
        // Éminence Facials & Body — Ottawa-only. When src param signals
        // that the client arrived from the Éminence page, hard-lock the
        // location to Ottawa (3) so the 3-clinic picker is hidden. See
        // `bookingLockLocationId` for full rationale.
        const src = (params.get("src") || "").toLowerCase();
        if (src.startsWith("eminence")) {
          setBookingLocationId("3");
          setBookingLockLocationId("3");
        }
        // Iter 293q · Feb 2026 · Karine ask — clicking a Popular Botox
        // Promo card on the client portal should drop the client straight
        // into the booking modal with THAT service pre-selected, not the
        // promo landing page. Supports `?service=<Tanya Catalog name>` and
        // `?package=<name>` (backward-compat alias).
        const svc = (params.get("service") || params.get("package") || "").trim();
        // Sept 4 2026 · Spin prize deep-link — /spin-and-win drops the
        // client here with `?prize_code=X&qualifying=hydrafacial,facial`.
        // Attach both to the booking flow so the code is auto-applied and
        // the treatment picker is pre-filtered to the qualifying list.
        const prizeCode = (params.get("prize_code") || params.get("code") || "").trim().toUpperCase();
        const qualifying = (params.get("qualifying") || "").trim();
        if (prizeCode) {
          setBookingPrizeCode(prizeCode);
          if (qualifying) {
            setBookingPrizeQualifying(qualifying.split(",").map(s => s.trim()).filter(Boolean));
          }
          // Auto-open the booking modal so the client lands mid-flow.
          setShowBookingModal(true);
        }
        // Iter 339 · Feb 2026 — August promo funnel wiring. When the
        // user arrives from /promo/laser-genesis-lymphatic-august, the
        // promo landing page stashes campaign metadata in sessionStorage
        // AND passes a `search=` param so the treatment picker filters
        // instantly to Laser Genesis. We also enrich the packageInfo
        // with variationHint + customerNote so Tanya's booking flow
        // pre-selects the exact combo when available.
        const searchQ = (params.get("search") || "").trim();
        let promoMeta = null;
        try {
          if (sessionStorage.getItem("kl_promo_campaign")) {
            promoMeta = {
              campaign: sessionStorage.getItem("kl_promo_campaign") || "",
              service: sessionStorage.getItem("kl_promo_service") || "",
              serviceSlug: sessionStorage.getItem("kl_promo_service_slug") || "",
              provider: sessionStorage.getItem("kl_promo_provider") || "",
              locationId: sessionStorage.getItem("kl_promo_location") || "",
              price: sessionStorage.getItem("kl_promo_price") || "",
              label: sessionStorage.getItem("kl_promo_label") || "",
            };
          }
        } catch { /* noop */ }
        if (svc) {
          const pkg = { name: svc, source: promoMeta ? "promo-landing" : "promo-card" };
          if (searchQ) pkg.searchQuery = searchQ;
          // Oct 14 2026 · exact Square variation id (Éminence one-tap Book)
          const variationQ = (params.get("variation") || "").trim();
          if (variationQ) pkg.variation_id = variationQ;
          // Iter 400 · Feb 18 2026 — optional `?note=<text>` param lets any
          // deep-link inject a customer note into the booking. Used by the
          // Latisse "Book my consultation" CTA on /shop/LATISSE-3ML so
          // Karine sees "Interested in Latisse" at Square checkout without
          // the client having to type anything.
          const noteQ = (params.get("note") || "").trim();
          if (noteQ) {
            pkg.customerNote = noteQ;
          }
          // Nov 2026 · keep ?src= (promo attribution) alive for the booking POST.
          try { const srcQ = (params.get("src") || "").trim(); if (srcQ) sessionStorage.setItem("kl_booking_src", srcQ); } catch { /* noop */ }
          // Iter 402.9 · Aug 2026 · Karine ask — Lumi's "Book directly"
          // CTA from a spa-ritual card passes `provider=aesthetician` so
          // the treatment picker knows to surface aesthetician availability
          // (facials) instead of injector availability (Botox / filler).
          // Falls through silently for any legacy links that don't set it.
          const providerQ = (params.get("provider") || "").trim().toLowerCase();
          if (providerQ) {
            // Same alias as above — "aesthetician" → "tanya" so downstream
            // matching (BookingCart Lumi hand-off etc.) uses the canonical slug.
            pkg.providerHint = providerQ === "aesthetician" ? "tanya" : providerQ;
          }
          const srcQ = (params.get("src") || "").trim();
          if (srcQ) {
            pkg.source = srcQ; // e.g. "lumi_advisor" for attribution
          }
          if (promoMeta) {
            pkg.campaign = promoMeta.campaign;
            // Iter 344 · Feb 12 2026 — variationHint depends on the
            // campaign so the auto-matcher looks for the right family.
            if (promoMeta.campaign === "glow-reset") {
              pkg.variationHint = "redensity";
            } else {
              pkg.variationHint = "laser genesis";
            }
            pkg.priceOverride = Number(promoMeta.price) || undefined;
            pkg.priceLabel = promoMeta.label || undefined;
            pkg.customerNote = promoMeta.campaign === "glow-reset"
              ? `GLOW RESET promo · $748 + tax · Redensity 1 3 mL + HydraFacial ($49 bonus, regular $170). Karine performs the Redensity injection; HydraFacial follow-up booked separately at Ottawa Prince of Wales by our team.`
              : `August Crown Rewards promo · ${promoMeta.label || "$75 + tax"} · Client selected Laser Genesis + Lymphatic Facial Drainage combo.`;
          }
          setBookingPackage(pkg);
        } else if (searchQ) {
          // Search-only prefill — no explicit service. Just pipe the
          // search string through so the picker filters immediately.
          setBookingPackage({ name: "", source: "search-prefill", searchQuery: searchQ });
        } else if ((params.get("filter") || "").trim()) {
          // Oct 29 2026 · menu-box "Book now": filtered picker, never auto-picks one item.
          setBookingPackage({ name: "", source: "search-prefill", searchQuery: params.get("filter").trim(), noAutoPick: true });
        }
        // "Book This Look" handoff from AI Skin Analysis — pull the
        // previewed treatments out of localStorage and turn them into a
        // packageInfo prefill so the treatment picker is auto-filled.
        // Payload TTL is 30 min — anything older is ignored so a stale
        // preview from days ago never surprises a returning visitor.
        try {
          const raw = localStorage.getItem("kl_ai_prefill_treatments");
          if (raw) {
            const payload = JSON.parse(raw);
            const fresh = payload?.timestamp && (Date.now() - payload.timestamp) < 30 * 60 * 1000;
            const list = Array.isArray(payload?.treatments) ? payload.treatments.filter(Boolean) : [];
            if (fresh && list.length > 0) {
              setBookingPackage({
                name: list[0],
                additional_treatments: list.slice(1),
                source: "ai-preview",
              });
            }
            localStorage.removeItem("kl_ai_prefill_treatments");
          }
        } catch { /* noop — booking still opens, just without prefill */ }
        setShowBookingModal(true);
        try { window.scrollTo({ top: 0, behavior: "instant" }); } catch { /* noop */ }
      } catch { /* noop */ }
    }
  }, [_location?.pathname, _location?.search]);

  useEffect(() => {
    // Fetch the live spot count (sum across all 3 duration buckets) so we can
    // show a "12 spots" badge on the hero button. Polls every 2 min.
    let cancelled = false;
    const load = () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/last-minute/diagnostics`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (cancelled || !d) return;
          const total = Object.values(d.buckets || {}).reduce(
            (sum, b) => sum + (b?.total || 0),
            0,
          );
          setLastMinuteCount(total);
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    const shouldOpenBooking = localStorage.getItem("open_booking");
    if (shouldOpenBooking) {
      localStorage.removeItem("open_booking");
      setTimeout(() => setShowBookingModal(true), 500);
    }
  }, []);

  // Global "open waitlist" event — so components anywhere (including the
  // booking-flow modal) can open the shared Waitlist modal without prop
  // drilling. Emits: window.dispatchEvent(new Event("open-waitlist"))
  //
  // Iter 172 — the event may now carry a `detail` payload with treatment
  // and location, which pre-fills the modal so service-page CTAs land
  // clients on the right form fields:
  //   window.dispatchEvent(new CustomEvent("open-waitlist",
  //     { detail: { treatment: "botox", location: "Ottawa" } }));
  useEffect(() => {
    const openWaitlistHandler = (e) => {
      const detail = e && e.detail ? e.detail : {};
      setWaitlistPrefill({
        treatment: detail.treatment || null,
        location: detail.location || null,
      });
      setShowWaitlistModal(true);
    };
    window.addEventListener("open-waitlist", openWaitlistHandler);
    return () => window.removeEventListener("open-waitlist", openWaitlistHandler);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, locationsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/locations`),
          axios.get(`${API}/testimonials`),
        ]);
        setServices(servicesRes.data);
        setLocations(locationsRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-open virtual consultation or referral if redirected from sub-page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("virtual") === "true") {
      setShowVirtualModal(true);
      window.history.replaceState({}, "", "/");
    }
    if (params.get("referral") === "true") {
      window.history.replaceState({}, "", "/");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-referral-popup'));
      }, 1000);
    }
    if (params.get("analysis") === "true") {
      window.history.replaceState({}, "", "/");
      setTimeout(() => setShowSkinAnalysis(true), 500);
    }
    if (params.get("facemap") === "true") {
      window.history.replaceState({}, "", "/");
      // `setShowFaceMap` was removed when the FaceMap modal was retired
      // (Feb 2026). Keep the URL clean-up but the trigger is a no-op now.
    }
    if (params.get("book") === "true") {
      window.history.replaceState({}, "", "/");
      setTimeout(() => setShowBookingModal(true), 500);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openBookingModal = () => {
    // If a provider is already prefilled (e.g. arrived via /book?provider=tanya
    // or a "Book with Tanya" CTA elsewhere on the page), skip the chooser and
    // go straight to the booking flow.
    if (bookingProvider) {
      setShowBookingModal(true);
    } else {
      setShowProviderChooser(true);
    }
  };

  // Called from ProviderChooserModal — sets the provider and opens the
  // full booking flow. Karine = all locations + full menu (no lock).
  // Tanya = Ottawa only + her treatment catalog (InlineBookingFlow already
  // locks location to id="3" when prefillProvider === "tanya").
  const handlePickProvider = (slug, extra) => {
    // Oct 20 2026 · three start choices (Find a treatment · Book with my
    // provider · Help me choose) + "Book my usual" for remembered clients.
    // Oct 21 2026 · search bar + Free consultation / Follow-up / Reschedule.
    setBookingSearch(null);
    if (slug === "reschedule") { setShowProviderChooser(false); navigate("/my-appointments"); return; }
    if (slug === "search") { setBookingProvider(null); setBookingType(null); setBookingSearch(extra || null); }
    else if (slug === "find") { setBookingProvider(null); setBookingType(null); }
    else if (slug === "help") { setBookingProvider("karine"); setBookingType("consultation"); }
    else if (slug === "followup") { setBookingProvider(null); setBookingType(null); setBookingSearch("follow"); }
    else if (slug === "usual") { setBookingProvider(null); setBookingType("usual"); }
    else { setBookingProvider(slug); setBookingType(null); }
    setShowProviderChooser(false);
    // Small delay so the chooser exit animation completes before the
    // booking modal slides in — feels much more polished than an abrupt swap.
    setTimeout(() => setShowBookingModal(true), 220);
  };

  return (
      <div className="min-h-screen bg-background" data-testid="home-page">
        <BlackFridayMobileSplash />
        <WelcomeRewardsPopup />
        {/* Oct 7 2026 · the fixed TopBar wrapper now reads the
            `--season-banner-h` CSS var so when the Fall/Winter promo
            banners are active, the TopBar + Navbar slide down to make
            room. Default (no banner) → top: 0. */}
        <div className="fixed left-0 right-0 z-50" style={{ top: "var(--season-banner-h, 0px)" }}>
          {location.pathname.startsWith("/preview/hero-v2") ? <TopBarV2 /> : <TopBar />}
        </div>
        {location.pathname.startsWith("/preview/hero-v2") ? (
          <NavbarV2 openBookingModal={openBookingModal} />
        ) : (
          <Navbar scrollToSection={scrollToSection} openBookingModal={openBookingModal} openVirtualConsultation={() => setShowVirtualModal(true)} openWaitlist={() => setShowWaitlistModal(true)} />
        )}
        {/* Spacer pushes in-flow content below the fixed TopBar + fixed Navbar.
            Mobile nav stack = TopBar 42 + Navbar 60 = ~102px.
            Desktop nav stack = TopBar 49 + Navbar 169 (secondary nav row!) = ~220px.
            Without this, AfterpayBanner + TrendingNowMarquee paint behind the
            fixed nav and become invisible. */}
        <div
          className={location.pathname.startsWith("/preview/hero-v2") ? "pt-[110px] md:pt-[260px]" : "pt-[101px] md:pt-[220px]"}
          aria-hidden="true"
        />
        {/* Oct 29 2026 · Karine — stationary photo menu boxes replaced the
            rolling Trending-Now pills; sit tight under the navbar. */}
        {!location.pathname.startsWith("/preview/hero-v2") && (
          <div className="bg-zinc-950 pt-3 pb-1.5" data-testid="home-treatment-menu">
            <TreatmentBoxMenu />
          </div>
        )}
        {!location.pathname.startsWith("/preview/hero-v2") && <AfterpayBanner />}
        {/* Returning-client welcome — quietly renders only when a signed-in
            client is due (or overdue) for their next visit. Non-signed-in
            visitors see nothing. See ReturningClientHero.jsx for cadence
            math. */}
        <ReturningClientHero />
        {/* "Trending Now" rolling banner — now includes BOTH priced trending
            items AND every treatment category we offer (interleaved). Karine
            asked us to merge instead of stacking two rolling bands.
            Desktop/tablet only — hidden on phones (too crowded). */}
        {/* Preview-only hero V2 (mockup at /preview/hero-v2). Live homepage
            still uses HeroSection below. Approve → swap one line. */}
        {location.pathname.startsWith("/preview/hero-v2") ? (
          <HeroV2Section
            openBookingModal={openBookingModal}
            openVirtualConsultation={() => setShowVirtualModal(true)}
          />
        ) : (
          <HeroSection
            scrollToSection={scrollToSection}
            openBookingModal={openBookingModal}
            openQuiz={() => setShowQuiz(true)}
            openVirtualConsultation={() => setShowVirtualModal(true)}
            openWaitlist={() => setShowWaitlistModal(true)}
            openLastMinuteClub={() => setShowLastMinuteClub(true)}
            lastMinuteCount={lastMinuteCount}
          />
        )}
        {/* Iter 460 · Feb 2026 · Karine ask — "Clients should easily be
            able to book Tanya or Karine and see at a glance the LMC
            available." Amber Karine card + sky-blue Tanya card, each
            with a live LMC preview + one-tap provider-scoped "Book"
            CTA. Placed immediately below the hero so it's the first
            actionable panel every visitor sees. Silently unmounts
            when both LMC buckets are empty (no filler noise). */}
        <LastMinuteAtAGlance
          onBookProvider={handlePickProvider}
          onOpenLastMinute={() => setShowLastMinuteClub(true)}
          onPickLmcSlot={(slot) => {
            setLastMinutePrefill(slot);
            setShowLastMinuteBooking(true);
          }}
        />
        {/* Iter 339 · Feb 2026 — Crown Rewards Members $75 Laser Genesis
            + Lymphatic Drainage August promo card. Slim, dismissable,
            deep-links to /promo/laser-genesis-lymphatic-august. */}
        <AugustCrownPromoCard />
        {/* Treatments grid moved up — clients see every treatment category
            immediately after the hero and can tap to browse / book. Previously
            this slot held the "4 Simple Steps to Your Results / Start Your
            Journey" ClientJourney component, which delayed treatment discovery. */}
        <TreatmentCategoriesGrid openBookingModal={openBookingModal} />
        {/* "Now Offering" rotation — surfaces newest services (RF Microneedling,
            HydraFacial, Laser Hair Removal) alongside core injectables, each
            with a short blurb and direct deep-link to that treatment page. */}
        <NowOfferingRotation />
        {/* Afterpay financing — placed immediately after treatment discovery so
            the "Pay in 4" message lands when visitors are pricing options.
            Previously only the slim top banner + tiny floating badge advertised
            this; the full info section was built but never mounted. */}
        <AfterpayInfoSection />
        <ServicesSection services={services} />
        <SeasonalSuggestions onBookNow={openBookingModal} scrollToSection={scrollToSection} />
        <TrustBar />
        <MostPopularSection />
        <OurPhilosophySection />
        <CredentialsBadges />
        <BrandsBanner />
        <PromotionsSection scrollToSection={scrollToSection} openBookingModal={openBookingModal} />
        <BundleBuilder />
        <BeforeAfterSlider />
        <BeautyHubHomeSection onOpenQuiz={() => setShowQuiz(true)} />
        <KarineDailyBlog />
        <AboutSection />
        
        {/* Karine's Daily Tip */}
        <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-amber-50/30">
          <div className="max-w-2xl mx-auto px-4">
            <KarineDailyTip />
          </div>
        </section>

        {/* Insider Tips subscribe card — high-conversion placement right after
            reading today's tip. Mobile users land on the homepage first, so
            this doubles as a discoverability replacement for the old sidebar. */}
        <section className="py-8 sm:py-12 bg-white" data-testid="home-insider-tips-signup">
          <div className="max-w-2xl mx-auto px-4">
            <DailyTipsPromo />
          </div>
        </section>
        
        <LocationsSection locations={locations} openBookingModal={openBookingModal} />
        <GoogleReviewsSection />
        <NewClientSection />

        {/* Rewards Promo */}
        <section className="py-10 sm:py-16 bg-black">
          <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center gap-6 sm:gap-8">
            <div className="w-48 sm:w-full md:w-1/3 flex-shrink-0 mx-auto md:mx-0">
              <a href="/rewards" className="block rounded-2xl overflow-hidden border border-amber-700/20 shadow-2xl hover:shadow-amber-900/20 transition-shadow">
                <img src="https://customer-assets.emergentagent.com/job_8bb38860-40a7-4ff4-a53a-04b11ae31460/artifacts/fs7d5hnt_2.png"
                  alt="Crown Reserve Points" className="w-full" />
              </a>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-amber-400 text-[10px] sm:text-xs uppercase tracking-[0.3em] mb-2">{language === "fr" ? "Crown Reserve" : "Crown Reserve"}</p>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {language === "fr" ? "Crown Reserve" : "Crown Reserve Points"}
              </h2>
              <p className="text-zinc-400 text-xs sm:text-sm mb-4 sm:mb-5 max-w-md mx-auto md:mx-0">
                {language === "fr"
                  ? "Gagnez des points à chaque visite, obtenez des bonus exclusifs et débloquez des récompenses VIP. Inscrivez-vous et recevez 1000 Crown Points gratuits!"
                  : "Earn points on every visit, unlock exclusive bonuses, and redeem for treatments. Sign up and get 1000 free Crown Points!"}
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a href="/rewards" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors" data-testid="home-rewards-link">
                  {language === "fr" ? "Voir les récompenses" : "View Rewards"}
                </a>
                <a href="/my-appointments" className="inline-flex items-center gap-2 border border-amber-600/40 text-amber-400 hover:bg-amber-900/20 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors">
                  {language === "fr" ? "S'inscrire" : "Sign Up"}
                </a>
              </div>
            </div>
          </div>
        </section>
        <CompactCommunityBanner onReferralClick={() => {
          const referralBtn = document.querySelector('[data-testid="navbar-refer-friend-btn"]');
          if (referralBtn) referralBtn.click();
        }} />
        {/* Live Facebook feed — for clients who don't have Facebook. */}
        <FacebookFeedWidget />
        <HomeSkincareTipsSection />
        <HomeCareersSection />
        <ContactSection services={services} locations={locations} />
        <FinalCTASection openBookingModal={openBookingModal} />
        <Footer scrollToSection={scrollToSection} />
        <BookingCart locations={locations} />
        {!showBookingModal && <StickyBookButton openBookingModal={openBookingModal} />}
        {!showBookingModal && <FloatingAfterpayBadge />}
        <PricingPickerPill />
        <FloatingSocialProof position="bottom-left" />
        <InlineBookingFlow
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false); setLastMinutePrefill(null); setBookingProvider(null); setBookingType(null); setBookingSearch(null); setBookingLocationId(null); setBookingLockLocationId(null); setBookingPackage(null); setBookingPrizeCode(null); setBookingPrizeQualifying(null);
            // Oct 20 2026 · Karine: closing a /book?… deep link (Winter banner,
            // shared treatment link) left the URL on /book, so a reload /
            // restored tab re-opened the booking window "again and again".
            try { if ((_location?.pathname || "").toLowerCase().startsWith("/book")) navigate("/", { replace: true }); } catch { /* noop */ }
          }}
          prefillProvider={bookingProvider}
          prefillBookingType={bookingType}
          prefillSearch={bookingSearch}
          prefillLocationId={bookingLocationId}
          lockLocationId={bookingLockLocationId}
          prefillPackage={bookingPackage}
          prefillPrizeCode={bookingPrizeCode}
          prefillQualifyingTreatments={bookingPrizeQualifying}
        />
        <ProviderChooserModal
          open={showProviderChooser}
          onClose={() => setShowProviderChooser(false)}
          onPick={handlePickProvider}
        />
        <LastMinuteClubModal
          open={showLastMinuteClub}
          onClose={() => setShowLastMinuteClub(false)}
          onPickSlot={(slot) => {
            setLastMinutePrefill(slot);
            setShowLastMinuteClub(false);
            // Open the lean Last Minute booking flow (NOT the full InlineBookingFlow).
            setTimeout(() => setShowLastMinuteBooking(true), 200);
          }}
        />
        <LastMinuteBookingFlow
          open={showLastMinuteBooking}
          slot={lastMinutePrefill}
          onClose={() => { setShowLastMinuteBooking(false); setLastMinutePrefill(null); }}
          onPickAlternative={(alt) => {
            // Swap the slot in-place and let the user keep going.
            setLastMinutePrefill(alt);
          }}
        />
        <TreatmentQuiz
          isOpen={showQuiz}
          onClose={() => setShowQuiz(false)}
          scrollToSection={scrollToSection}
        />
        <VirtualConsultationModal
          isOpen={showVirtualModal}
          onClose={() => setShowVirtualModal(false)}
        />
        <WaitlistModal
          isOpen={showWaitlistModal}
          onClose={() => setShowWaitlistModal(false)}
          prefilledTreatment={waitlistPrefill.treatment}
          prefilledLocation={waitlistPrefill.location}
        />
        <SocialProofNotification />
        <Suspense fallback={null}>
          <AISkinAnalysis
            isOpen={showSkinAnalysis}
            onClose={() => setShowSkinAnalysis(false)}
          />
        </Suspense>
        
        {/* Luxury Floating Action Buttons - Rich Gold & Chocolate Theme
            iPad-optimized (Feb 24 2026): Karine reported the fixed
            cards on the right were covering the hero title on iPad
            landscape. We now shrink every card at the md-lg range so
            the hero text has room to breathe; full luxe width returns
            at xl (≥ 1280 px). */}
        <div className="hidden md:flex fixed bottom-6 right-6 z-40 flex-col gap-2 xl:gap-3 kl-scroll-fade">
          {/* Crown Reserve Button */}
          <Link to="/crown-reserve-hub" onClick={() => window.scrollTo(0, 0)}>
            <div
              className="relative w-52 xl:w-64 px-3.5 xl:px-5 py-2.5 xl:py-3.5 rounded-lg flex flex-col items-center justify-center gap-0.5 group overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(196,164,82,0.4)] cursor-pointer"
              style={{
                background: 'linear-gradient(145deg, #1a1a1a 0%, #0a0a0a 50%, #1a1a1a 100%)',
                border: '2px solid transparent',
                borderImage: 'linear-gradient(145deg, #c4a452, #dfc06e, #b8942f, #dfc06e, #c4a452) 1',
                boxShadow: '0 4px 20px rgba(196,164,82,0.25), inset 0 1px 0 rgba(223,192,110,0.1)',
              }}
              data-testid="floating-crown-reserve-button"
            >
              <div className="absolute inset-[2px] rounded-md" style={{ background: 'linear-gradient(145deg, #111 0%, #0a0a0a 100%)' }} />
              <div className="relative flex items-center gap-2">
                <Crown className="w-4 h-4 xl:w-5 xl:h-5" style={{ color: '#dfc06e' }} />
                <span className="text-sm xl:text-base font-bold tracking-wide" style={{ color: '#dfc06e', fontFamily: "'Playfair Display', serif" }}>
                  Crown Reserve
                </span>
              </div>
              <span className="relative text-[10px] xl:text-[11px] tracking-wider" style={{ color: '#b8942f' }}>
                Points & Monthly Plans
              </span>
            </div>
          </Link>

          {/* AI Photo Analysis Button · Aurora (Feb 2026) — reworded
              per Karine's Feb 14 ask to make the value prop obvious.
              Two-line label ("Photo Analysis" + "See your results before
              treatment") + a bright animated aurora gradient to stand
              out from the chocolate / gold buttons on the page. */}
          <button
            onClick={() => (visionLumiFace === 0 ? setShowSkinAnalysis(true) : window.dispatchEvent(new Event("open-lumi")))}
            onMouseEnter={_prewarmFacialVisionAI}
            onFocus={_prewarmFacialVisionAI}
            onTouchStart={_prewarmFacialVisionAI}
            className={`luxury-glass-btn ${visionLumiFace === 0 ? "luxury-btn-aurora" : ""} w-52 xl:w-64 px-3.5 xl:px-5 py-2.5 xl:py-3 rounded-2xl flex items-center gap-2.5 xl:gap-3 group relative overflow-hidden text-left transition-colors duration-700`}
            style={visionLumiFace === 1 ? { background: "linear-gradient(135deg, #b45309 0%, #e11d48 55%, #be185d 100%)", color: "#fff" } : undefined}
            data-testid="floating-skin-analysis-button"
            data-active-face={visionLumiFace === 0 ? "facial-vision" : "lumi"}
            aria-label={visionLumiFace === 0
              ? (language === "fr" ? "Analyse photo — voyez vos résultats avant traitement" : "Photo analysis — see your results before treatment")
              : (language === "fr" ? "Parler à Lumi — votre spécialiste en soins de la peau" : "Ask Lumi — your skincare specialist")}
          >
            {visionLumiFace === 0 && <span className="aurora-icon-halo" aria-hidden="true" />}
            <span className="relative flex items-center justify-center w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-white/20 flex-shrink-0 backdrop-blur-sm">
              {visionLumiFace === 0
                ? <Camera className="w-4 h-4 xl:w-5 xl:h-5" />
                : <span className="text-base xl:text-lg leading-none" aria-hidden="true">✨</span>}
            </span>
            <span key={visionLumiFace} className="flex flex-col leading-tight relative min-w-0 animate-fade-in">
              <span className="text-[13px] xl:text-[15px] font-bold tracking-tight">
                {visionLumiFace === 0 ? "FacialVisionAI" : (language === "fr" ? "Demandez à Lumi" : "Ask Lumi")}
              </span>
              <span className="text-[9px] xl:text-[10.5px] font-medium uppercase tracking-widest opacity-95">
                {visionLumiFace === 0
                  ? (language === "fr" ? "Voyez vos résultats · Instantané" : "See Your Results · Instant")
                  : (language === "fr" ? "Spécialiste soins de la peau · 24/7" : "Skincare Specialist · 24/7")}
              </span>
            </span>
            {/* subtle shimmer sweep on hover */}
            <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
            <span className="absolute bottom-1 right-2.5 flex gap-1" aria-hidden="true">
              <span className={`w-1.5 h-1.5 rounded-full ${visionLumiFace === 0 ? "bg-white" : "bg-white/40"}`} />
              <span className={`w-1.5 h-1.5 rounded-full ${visionLumiFace === 1 ? "bg-white" : "bg-white/40"}`} />
            </span>
          </button>

          {/* Rotating Tile (Jun 22, 2026): Skin Tip ⟷ Promotions every ~9s.
              Each face is a real anchor so taps still route fast. Crossfade
              via absolute-positioned layers; a tiny dot indicator at the
              bottom shows the live face. */}
          <div
            className="relative w-40 h-40 xl:w-48 xl:h-48 group"
            data-testid="floating-rotating-tile"
            data-active-face={
              tileIndex === 0 ? "skin-tip" :
              tileIndex === 1 ? "promotions" :
              tileIndex === 2 ? "glp1" :
              tileIndex === 3 ? "menopause" :
              tileIndex === 4 ? "andropause" :
              tileIndex === 5 ? "prevention" :
              tileIndex === 6 ? "womens-mens-health" :
              tileIndex === 7 ? "ai-future-self" :
              "eminence-facials"
            }
          >
            {/* Face 1 — Karine's 1-Minute Skin Tip */}
            <a
              href="/aesthetic-guide"
              aria-hidden={tileIndex !== 0}
              tabIndex={tileIndex === 0 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(0,0,0,0.4)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 0 ? 1 : 0,
                pointerEvents: tileIndex === 0 ? "auto" : "none",
              }}
              data-testid="floating-aesthetic-guide-button"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/5jnl5i2k_Untitled%20%28Website%29%20%281%29.png"
                alt="Karine's 1 Minute Skin Tip - Higher Education"
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-[10%] left-0 right-0 text-center text-white text-[11px] font-medium tracking-wide pointer-events-none bg-[#2a9d8f]/90 py-0.5 mx-[8%] rounded" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            </a>

            {/* Face 2 — Today's Promotions */}
            <Link
              to="/promotions"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 1}
              tabIndex={tileIndex === 1 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(196,164,82,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 1 ? 1 : 0,
                pointerEvents: tileIndex === 1 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #2a1a08 0%, #0c0603 70%), " +
                  "linear-gradient(145deg, #1a1208 0%, #0a0504 100%)",
                border: "1.5px solid rgba(223,192,110,0.55)",
              }}
              data-testid="floating-promotions-button"
            >
              {/* Champagne sheen sweep on hover */}
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(135deg, transparent 30%, rgba(223,192,110,0.18) 50%, transparent 70%)",
                }}
              />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span
                  className="text-[10px] uppercase tracking-[3px] mb-1"
                  style={{ color: "#b8942f", fontFamily: "'Inter', sans-serif" }}
                >
                  This Week
                </span>
                <span
                  className="text-3xl leading-none font-bold"
                  style={{ color: "#dfc06e", fontFamily: "'Playfair Display', serif" }}
                >
                  Promotions
                </span>
                <span
                  className="text-[10px] uppercase tracking-[2px] mt-1.5"
                  style={{ color: "#c4a452" }}
                >
                  & Crown Deals
                </span>
                <span
                  className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full"
                  style={{
                    background: "linear-gradient(145deg, #dfc06e, #b8942f)",
                    color: "#1a0d05",
                    fontWeight: 700,
                  }}
                >
                  Tap to View →
                </span>
                <span
                  className="absolute bottom-2 left-0 right-0 text-center text-[9px]"
                  style={{ color: "#8a6b1f" }}
                >
                  {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </span>
              </div>
            </Link>

            {/* Face 3 — GLP-1 Lift & Restore Program (Feb 2026). */}
            <Link
              to="/lift-restore-program"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 2}
              tabIndex={tileIndex === 2 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(244,114,182,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 2 ? 1 : 0,
                pointerEvents: tileIndex === 2 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #4a1a3a 0%, #1a0714 70%), " +
                  "linear-gradient(145deg, #3a1030 0%, #0f0410 100%)",
                border: "1.5px solid rgba(244,114,182,0.55)",
              }}
              data-testid="floating-glp1-button"
            >
              <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(135deg, transparent 30%, rgba(244,114,182,0.18) 50%, transparent 70%)" }} />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span className="text-[9px] uppercase tracking-[2.5px] mb-1" style={{ color: "#f9a8d4", fontFamily: "'Inter', sans-serif" }}>GLP-1 · Ozempic</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#fbcfe8", fontFamily: "'Playfair Display', serif" }}>Lift &amp;</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#fbcfe8", fontFamily: "'Playfair Display', serif" }}>Restore</span>
                <span className="text-[9px] uppercase tracking-[2px] mt-1.5" style={{ color: "#e9addc" }}>3-Phase Program</span>
                <span className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full" style={{ background: "linear-gradient(145deg, #f472b6, #be185d)", color: "#1a0714", fontWeight: 700 }}>Explore →</span>
                <span className="absolute bottom-2 left-0 right-0 text-center text-[9px]" style={{ color: "#c084a8" }}>Restore · Rebuild · Refine</span>
              </div>
            </Link>

            {/* Face 4 — Menopause Restore Program (rose/pink palette). */}
            <Link
              to="/menopause-program"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 3}
              tabIndex={tileIndex === 3 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(251,113,133,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 3 ? 1 : 0,
                pointerEvents: tileIndex === 3 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #4a1420 0%, #1a0810 70%), " +
                  "linear-gradient(145deg, #3a1018 0%, #0f0308 100%)",
                border: "1.5px solid rgba(251,113,133,0.55)",
              }}
              data-testid="floating-menopause-button"
            >
              <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(135deg, transparent 30%, rgba(251,113,133,0.18) 50%, transparent 70%)" }} />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span className="text-[9px] uppercase tracking-[2.5px] mb-1" style={{ color: "#fda4af", fontFamily: "'Inter', sans-serif" }}>Women 45+</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#fecdd3", fontFamily: "'Playfair Display', serif" }}>Menopause</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#fecdd3", fontFamily: "'Playfair Display', serif" }}>Restore</span>
                <span className="text-[9px] uppercase tracking-[2px] mt-1.5" style={{ color: "#f9a8b4" }}>3 Stages · From $1,499</span>
                <span className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full" style={{ background: "linear-gradient(145deg, #fb7185, #be123c)", color: "#1a0714", fontWeight: 700 }}>Explore →</span>
                <span className="absolute bottom-2 left-0 right-0 text-center text-[9px]" style={{ color: "#c48598" }}>Rebuild collagen · Restore glow</span>
              </div>
            </Link>

            {/* Face 5 — Andropause Restore Program (steel/blue palette). */}
            <Link
              to="/andropause-program"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 4}
              tabIndex={tileIndex === 4 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(59,130,246,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 4 ? 1 : 0,
                pointerEvents: tileIndex === 4 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #10243a 0%, #050b18 70%), " +
                  "linear-gradient(145deg, #0c1e32 0%, #030811 100%)",
                border: "1.5px solid rgba(96,165,250,0.55)",
              }}
              data-testid="floating-andropause-button"
            >
              <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(135deg, transparent 30%, rgba(96,165,250,0.18) 50%, transparent 70%)" }} />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span className="text-[9px] uppercase tracking-[2.5px] mb-1" style={{ color: "#93c5fd", fontFamily: "'Inter', sans-serif" }}>Men 40+</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#bfdbfe", fontFamily: "'Playfair Display', serif" }}>Andropause</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#bfdbfe", fontFamily: "'Playfair Display', serif" }}>Restore</span>
                <span className="text-[9px] uppercase tracking-[2px] mt-1.5" style={{ color: "#93c5fd" }}>Jawline · PRP · Discreet</span>
                <span className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full" style={{ background: "linear-gradient(145deg, #60a5fa, #1d4ed8)", color: "#050b18", fontWeight: 700 }}>Explore →</span>
                <span className="absolute bottom-2 left-0 right-0 text-center text-[9px]" style={{ color: "#93b4d1" }}>Strong · Confident · Yours</span>
              </div>
            </Link>

            {/* Face 6 — Prevention · Age Well Timeline (emerald/teal palette). */}
            <Link
              to="/prevention"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 5}
              tabIndex={tileIndex === 5 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(52,211,153,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 5 ? 1 : 0,
                pointerEvents: tileIndex === 5 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #0d3a2f 0%, #041814 70%), " +
                  "linear-gradient(145deg, #0a2e26 0%, #030f0c 100%)",
                border: "1.5px solid rgba(52,211,153,0.55)",
              }}
              data-testid="floating-prevention-button"
            >
              {/* Faded woman's-face background — evokes the "prevention · every
                  decade" story. Sits above the base gradient, below the copy. */}
              <span
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: "url(/generated/future-self-mid40s.png)",
                  backgroundSize: "cover",
                  backgroundPosition: "center 25%",
                  opacity: 0.4,
                  mixBlendMode: "luminosity",
                }}
              />
              {/* Emerald tint scrim so copy stays legible on the portrait */}
              <span
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(4,24,20,0.45) 0%, rgba(4,24,20,0.75) 100%)",
                }}
              />
              <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(135deg, transparent 30%, rgba(52,211,153,0.18) 50%, transparent 70%)" }} />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span className="text-[9px] uppercase tracking-[2.5px] mb-1" style={{ color: "#6ee7b7", fontFamily: "'Inter', sans-serif", textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>20s · 30s · 40s · 50s+</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#a7f3d0", fontFamily: "'Playfair Display', serif", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>Age Well</span>
                <span className="text-2xl leading-none font-bold" style={{ color: "#a7f3d0", fontFamily: "'Playfair Display', serif", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>Timeline</span>
                <span className="text-[8.5px] uppercase tracking-[2px] mt-1.5" style={{ color: "#6ee7b7", textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>Right treatment · Right decade</span>
                <span className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full" style={{ background: "linear-gradient(145deg, #34d399, #047857)", color: "#041814", fontWeight: 700, boxShadow: "0 4px 12px rgba(4,24,20,0.5)" }}>Explore →</span>
              </div>
            </Link>

            {/* Face 7 — Women's & Men's Health · Complete Approach campaign. */}
            <Link
              to="/menopause-program"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 6}
              tabIndex={tileIndex === 6 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(244,114,182,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 6 ? 1 : 0,
                pointerEvents: tileIndex === 6 ? "auto" : "none",
              }}
              data-testid="floating-womens-mens-health-button"
            >
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/ko4gngl9_image.png"
                alt="Focusing on Women's & Men's Health — Menopause & Andropause Programs"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-2.5 text-center">
                <span className="block text-[9px] uppercase tracking-[2px] font-semibold text-rose-200/95" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Women&rsquo;s &amp; Men&rsquo;s Health
                </span>
                <span className="block text-white text-[11px] font-bold leading-tight mt-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Prevent · Restore · Thrive
                </span>
                <span className="mt-1.5 inline-block text-[9px] uppercase tracking-[1.5px] px-2 py-0.5 rounded-full" style={{ background: "linear-gradient(145deg, #fda4af, #be123c)", color: "#1a0714", fontWeight: 700 }}>
                  Explore →
                </span>
              </div>
            </Link>

            {/* Face 8 — AI Future Face · Skin Analysis teaser (Feb 6 2026).
                Karine ask: push AI exposure hard. Rotates through 11 split-face
                BEFORE/AFTER teasers (aging, brow-lift, filler, lips, jawline,
                skin tone, male variants). Tapping opens the AI Skin Analysis
                modal directly. Uses the same shared catalogue as the portal
                FutureSelfPeekCard + lobby TV signage. */}
            <AIFutureRotatingFace
              active={tileIndex === 7}
              language={language}
              onOpen={() => setShowSkinAnalysis(true)}
            />

            {/* Face 9 — Éminence Organic Facials & Body (Feb 2026).
                New retail-service line: certified-organic facials + body
                rituals hand-crafted from fresh botanicals. Deep-forest
                palette, botanical leaf micro-motif. Ottawa only. */}
            <Link
              to="/treatments/eminence-facials"
              onClick={() => window.scrollTo(0, 0)}
              aria-hidden={tileIndex !== 8}
              tabIndex={tileIndex === 8 ? 0 : -1}
              className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(110,231,183,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
              style={{
                opacity: tileIndex === 8 ? 1 : 0,
                pointerEvents: tileIndex === 8 ? "auto" : "none",
                background:
                  "radial-gradient(circle at 35% 25%, #143026 0%, #050f0b 70%), " +
                  "linear-gradient(145deg, #0f2a20 0%, #030a07 100%)",
                border: "1.5px solid rgba(110,231,183,0.55)",
              }}
              data-testid="floating-eminence-facials-button"
            >
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(135deg, transparent 30%, rgba(110,231,183,0.18) 50%, transparent 70%)",
                }}
              />
              <div className="relative h-full w-full flex flex-col items-center justify-center px-3 text-center">
                <span
                  className="text-[9px] uppercase tracking-[2.5px] mb-1"
                  style={{ color: "#a7f3d0", fontFamily: "'Inter', sans-serif" }}
                >
                  New · Organic
                </span>
                <span
                  className="text-2xl leading-none font-bold"
                  style={{ color: "#d1fae5", fontFamily: "'Playfair Display', serif" }}
                >
                  Éminence
                </span>
                <span
                  className="text-[13px] leading-tight font-medium mt-1"
                  style={{ color: "#d1fae5", fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
                >
                  Facials &amp; Body
                </span>
                <span
                  className="text-[9px] uppercase tracking-[2px] mt-1.5"
                  style={{ color: "#a7f3d0" }}
                >
                  Ottawa · From $125
                </span>
                <span
                  className="mt-3 text-[10px] uppercase tracking-[1.5px] px-3 py-1 rounded-full"
                  style={{
                    background: "linear-gradient(145deg, #34d399, #047857)",
                    color: "#04140d",
                    fontWeight: 700,
                  }}
                >
                  Explore →
                </span>
                <span
                  className="absolute bottom-2 left-0 right-0 text-center text-[9px]"
                  style={{ color: "#7fbf9c" }}
                >
                  Fresh fruit · herbs · botanicals
                </span>
              </div>
            </Link>

            {/* Face 10 — Black Friday teaser poster (Nov 3 2026 · Karine). Auto-hides Nov 28 2026. */}
            {bfTeaser && (
              <Link
                to="/promotions#black-friday"
                aria-hidden={tileIndex !== 9}
                tabIndex={tileIndex === 9 ? 0 : -1}
                className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(223,192,110,0.45)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl"
                style={{ opacity: tileIndex === 9 ? 1 : 0, pointerEvents: tileIndex === 9 ? "auto" : "none", border: "1.5px solid rgba(223,192,110,0.6)", background: "#000" }}
                data-testid="floating-black-friday-teaser"
                aria-label="Black Friday is coming — Friday, November 27 — stay tuned"
              >
                <img src={BLACK_FRIDAY_TEASER_IMG} alt="Black Friday is coming — Friday, November 27 — stay tuned" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </Link>
            )}

            {/* Tiny dot indicator — one per rotating face. */}
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
              {Array.from({ length: tileCount }, (_, i) => i).map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                  style={{ background: tileIndex === i ? "#dfc06e" : "rgba(223,192,110,0.3)" }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Quick Actions - replaces hidden floating buttons on mobile */}
        <MobileQuickActions
          onSkinAnalysis={() => setShowSkinAnalysis(true)}
          onFaceMap={() => { /* FaceMap retired Feb 2026 · no-op to avoid crash */ }}
        />
      </div>
  );
};

export default HomePage;
